import React, { useState, useEffect } from 'react';
import { Plus, Save, X, AlertCircle, Brain, Settings, TrendingUp } from 'lucide-react';
import apiService from '../../../services/api';
import { AnalysisFramework, ThreatCategory } from '../../../types/prompt';

interface NotificationState {
  type: 'success' | 'error' | null;
  message: string;
}

interface DetectionPattern {
  name: string;
  pattern: string;
  weight: number;
  description: string;
}

interface CategoryWeight {
  categoryCode: string;
  weight: number;
  threshold: number;
}

export default function AnalysisFrameworkEditor() {
  const [frameworks, setFrameworks] = useState<AnalysisFramework[]>([]);
  const [categories, setCategories] = useState<ThreatCategory[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<AnalysisFramework | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<NotificationState>({ type: null, message: '' });
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    methodology: '',
    detectionPatterns: [] as DetectionPattern[],
    categoryWeights: [] as CategoryWeight[],
    overallThreshold: 5.0,
    confidenceThreshold: 0.7,
    isActive: true,
  });

  const [newPattern, setNewPattern] = useState({
    name: '',
    pattern: '',
    weight: 1.0,
    description: '',
  });

  const [showPatternForm, setShowPatternForm] = useState(false);

  useEffect(() => {
    fetchFrameworks();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (notification.type) {
      const timer = setTimeout(() => {
        setNotification({ type: null, message: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchFrameworks = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/admin/prompts/frameworks');
      setFrameworks(response.data);
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to load frameworks' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.get('/api/threat-categories');
      setCategories(response.data);
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to load threat categories' });
    }
  };

  const handleSelectFramework = (framework: AnalysisFramework) => {
    setSelectedFramework(framework);
    setFormData({
      code: framework.code,
      name: framework.name,
      description: framework.description || '',
      methodology: framework.methodology || '',
      detectionPatterns: framework.detectionPatterns || [],
      categoryWeights: framework.categoryWeights || [],
      overallThreshold: framework.overallThreshold || 5.0,
      confidenceThreshold: framework.confidenceThreshold || 0.7,
      isActive: framework.isActive,
    });
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        await apiService.post('/api/admin/prompts/frameworks', formData);
        setNotification({ type: 'success', message: 'Framework created successfully' });
      } else if (selectedFramework) {
        await apiService.put(`/api/admin/prompts/frameworks/${selectedFramework.id}`, formData);
        setNotification({ type: 'success', message: 'Framework updated successfully' });
      }
      
      fetchFrameworks();
      resetForm();
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to save framework' });
    }
  };

  const resetForm = () => {
    setIsCreating(false);
    setSelectedFramework(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      methodology: '',
      detectionPatterns: [],
      categoryWeights: [],
      overallThreshold: 5.0,
      confidenceThreshold: 0.7,
      isActive: true,
    });
    setShowPatternForm(false);
    setNewPattern({ name: '', pattern: '', weight: 1.0, description: '' });
  };

  const handleAddPattern = () => {
    if (!newPattern.name || !newPattern.pattern) {
      setNotification({ type: 'error', message: 'Pattern name and pattern text are required' });
      return;
    }

    setFormData({
      ...formData,
      detectionPatterns: [...formData.detectionPatterns, { ...newPattern }],
    });
    
    setNewPattern({ name: '', pattern: '', weight: 1.0, description: '' });
    setShowPatternForm(false);
  };

  const handleRemovePattern = (index: number) => {
    setFormData({
      ...formData,
      detectionPatterns: formData.detectionPatterns.filter((_, i) => i !== index),
    });
  };

  const handleUpdateCategoryWeight = (categoryCode: string, field: 'weight' | 'threshold', value: number) => {
    const existing = formData.categoryWeights.find(cw => cw.categoryCode === categoryCode);
    
    if (existing) {
      setFormData({
        ...formData,
        categoryWeights: formData.categoryWeights.map(cw =>
          cw.categoryCode === categoryCode ? { ...cw, [field]: value } : cw
        ),
      });
    } else {
      setFormData({
        ...formData,
        categoryWeights: [...formData.categoryWeights, {
          categoryCode,
          weight: field === 'weight' ? value : 1.0,
          threshold: field === 'threshold' ? value : 5.0,
        }],
      });
    }
  };

  const getCategoryWeight = (categoryCode: string): CategoryWeight => {
    return formData.categoryWeights.find(cw => cw.categoryCode === categoryCode) || {
      categoryCode,
      weight: 1.0,
      threshold: 5.0,
    };
  };

  return (
    <div className="p-6">
      {/* Help Section */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">What are Analysis Frameworks?</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
          Analysis Frameworks define HOW the AI analyzes articles for threats. They contain:
        </p>
        <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 ml-4">
          <li>• <strong>Detection Patterns</strong>: What to look for in articles (e.g., "military buildup", "cyber attacks")</li>
          <li>• <strong>Category Weights</strong>: How important each threat category is (MILITARY, CYBER, etc.)</li>
          <li>• <strong>Alert Thresholds</strong>: When to trigger alerts for each category (0-10 scale)</li>
          <li>• <strong>Analysis Methodology</strong>: The overall approach and focus of the analysis</li>
        </ul>
        <p className="text-sm text-gray-900 dark:text-gray-100 mt-3 font-medium">
          Frameworks are used during Stage 2 AI analysis to score articles and determine threat levels.
        </p>
      </div>

      {/* Notification */}
      {notification.type && (
        <div className={`mb-4 p-4 rounded-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
            : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
        }`}>
          {notification.message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Framework List */}
        <div className="lg:col-span-1">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Analysis Frameworks
            </h3>
            <button
              onClick={() => {
                setIsCreating(true);
                setSelectedFramework(null);
                resetForm();
              }}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              New
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading frameworks...</div>
          ) : (
            <div className="space-y-2">
              {frameworks.map((framework) => (
                <button
                  key={framework.id}
                  onClick={() => handleSelectFramework(framework)}
                  className={`
                    w-full text-left p-3 rounded-lg border transition-colors
                    ${selectedFramework?.id === framework.id
                      ? 'border-blue-500 bg-blue-100'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {framework.name}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {framework.code}
                        </span>
                        {framework.isActive && (
                          <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                            Active
                          </span>
                        )}
                      </div>
                    </div>
                    <Brain className="w-4 h-4 text-gray-400" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Framework Editor */}
        <div className="lg:col-span-2">
          {(selectedFramework || isCreating) ? (
            <div className="space-y-6">
              {/* Editor Header */}
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isCreating ? 'Create Framework' : 'Edit Framework'}
                </h3>
                <button
                  onClick={resetForm}
                  className="p-1 text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Basic Info */}
              <div className="space-y-4">
                {isCreating && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Code (unique identifier)
                    </label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      placeholder="e.g., estonia_invasion_detection"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Analysis Methodology
                  </label>
                  <textarea
                    value={formData.methodology}
                    onChange={(e) => setFormData({ ...formData, methodology: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder="Describe the analysis approach, pattern recognition methods, and scoring criteria..."
                  />
                </div>
              </div>

              {/* Detection Patterns */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Detection Patterns
                  </h4>
                  <button
                    onClick={() => setShowPatternForm(true)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Pattern
                  </button>
                </div>

                {showPatternForm && (
                  <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h5 className="font-medium mb-3">New Detection Pattern</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Pattern Name
                        </label>
                        <input
                          type="text"
                          value={newPattern.name}
                          onChange={(e) => setNewPattern({ ...newPattern, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="e.g., Military Buildup"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Weight (0-5)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="5"
                          step="0.1"
                          value={newPattern.weight}
                          onChange={(e) => setNewPattern({ ...newPattern, weight: parseFloat(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Pattern Description
                        </label>
                        <textarea
                          value={newPattern.pattern}
                          onChange={(e) => setNewPattern({ ...newPattern, pattern: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="Describe what to look for in articles..."
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Additional Notes
                        </label>
                        <input
                          type="text"
                          value={newPattern.description}
                          onChange={(e) => setNewPattern({ ...newPattern, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="Optional context or examples"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-2 mt-4">
                      <button
                        onClick={() => setShowPatternForm(false)}
                        className="px-3 py-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddPattern}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Add Pattern
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {formData.detectionPatterns.map((pattern, index) => (
                    <div key={index} className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-medium text-gray-900 dark:text-white">{pattern.name}</h5>
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                              Weight: {pattern.weight}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{pattern.pattern}</p>
                          {pattern.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-500">{pattern.description}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemovePattern(index)}
                          className="ml-2 p-1 text-red-500 hover:text-red-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category Weights */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
                  <TrendingUp className="w-4 h-4" />
                  Category Weights & Thresholds
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {categories.map((category) => {
                    const weight = getCategoryWeight(category.code);
                    return (
                      <div key={category.id} className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <h5 className="font-medium text-gray-900 dark:text-white mb-2">{category.name}</h5>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                              Weight (importance)
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="3"
                              step="0.1"
                              value={weight.weight}
                              onChange={(e) => handleUpdateCategoryWeight(category.code, 'weight', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                              Alert Threshold (0-10)
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="10"
                              step="0.1"
                              value={weight.threshold}
                              onChange={(e) => handleUpdateCategoryWeight(category.code, 'threshold', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Global Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Overall Risk Threshold (0-10)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.1"
                    value={formData.overallThreshold}
                    onChange={(e) => setFormData({ ...formData, overallThreshold: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confidence Threshold (0-1)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={formData.confidenceThreshold}
                    onChange={(e) => setFormData({ ...formData, confidenceThreshold: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2">
                <button
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  Save Framework
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              Select a framework to edit or create a new one
            </div>
          )}
        </div>
      </div>
    </div>
  );
}